import React, { useMemo } from 'react';
import type { FC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import type { ButtonProps } from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import FocusRing from '@atlaskit/focus-ring';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, xcss } from '@atlaskit/primitives';

import type { Pill } from '@atlassian/pill-selector';
import { PillSelector } from '@atlassian/pill-selector';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';

import { DialogPillName, TestId } from '../dialogConstants.tsx';

import * as S from './styled.tsx';

const i18n = defineMessages({
	dialogHeader: {
		id: 'confluence-analytics.dialog.header',
		defaultMessage: 'Analytics',
		description: 'Text to display in the dialog when there is no pills to show',
	},
	dialogCloseAriaLabel: {
		id: 'confluence-analytics.dialog.close-aria-label',
		defaultMessage: 'Close Confluence Analytics nonmodal dialog',
		description: 'Label for a button to close the analytics dialog.',
	},
	dialogCloseLabel: {
		id: 'confluence-analytics.dialog.close-label',
		defaultMessage: 'Close modal',
		description: 'Label for a button to close the analytics dialog.',
	},
	filterViewed: {
		id: 'confluence-analytics.filters.views',
		defaultMessage: 'Views',
		description: 'Name of the tab that shows a graph and list of who has viewed the page.',
	},
	filterMentioned: {
		id: 'confluence-analytics.filters.mentions',
		defaultMessage: 'Mentions',
		description: 'Name of the tab that shows a progress bar and list of mentioned users.',
	},
	filterLinks: {
		id: 'confluence-analytics.filters.links',
		defaultMessage: 'Links',
		description:
			'Name of the tab that shows a list of links that are inbound to or outbound from the current content.',
	},
});

const PillSelectorContainerStyles = xcss({
	marginTop: 'space.100',
	marginBottom: 'space.100',
});

export type AnalyticsDialogHeaderProps = {
	error?: ApolloErrorInterop;
	isLoading: boolean;
	selectedPill: DialogPillName;
	shouldShowPills: boolean;
	isPrivacyModeEnabled?: boolean;
	onPillSelected?: (pill: DialogPillName) => void;
	onClose?: ButtonProps['onClick'];
};

export const AnalyticsDialogHeader: FC<AnalyticsDialogHeaderProps> = ({
	error,
	isLoading,
	selectedPill,

	isPrivacyModeEnabled,
	onPillSelected = () => {},
	onClose,
}) => {
	const intl = useIntl();
	const { edition } = useSessionData();

	const shouldShowLoadingState = useMemo(() => isLoading || !!error, [isLoading, error]);

	const closeButton = (
		<FocusRing>
			<S.CloseButton appearance="link" onClick={onClose} testId={TestId.CLOSE_BUTTON}>
				<CrossIcon
					spacing="spacious"
					label={intl.formatMessage(i18n.dialogCloseLabel)}
					color={token('color.icon')}
				/>
			</S.CloseButton>
		</FocusRing>
	);

	const dialogPills: Pill<DialogPillName>[] = [
		{
			name: DialogPillName.VIEWS,
			content: <FormattedMessage {...i18n.filterViewed} />,
		},
		...(edition === ConfluenceEdition.PREMIUM
			? [
					...(!isPrivacyModeEnabled
						? [
								{
									name: DialogPillName.MENTIONS,
									content: <FormattedMessage {...i18n.filterMentioned} />,
								},
							]
						: []),
					{
						name: DialogPillName.LINKS,
						content: <FormattedMessage {...i18n.filterLinks} />,
					},
				]
			: []),
	];

	return (
		<>
			<Inline spread="space-between">
				<Heading size="medium" as="h2">
					<FormattedMessage {...i18n.dialogHeader} />
				</Heading>
				{closeButton}
			</Inline>
			<Inline xcss={PillSelectorContainerStyles}>
				<PillSelector
					isLoading={shouldShowLoadingState}
					pills={dialogPills}
					selectedPill={selectedPill}
					testId={TestId.PILLS}
					onPillSelected={onPillSelected}
				/>
			</Inline>
		</>
	);
};
